import { _ } from "../polyglot/polyglot.js";
import abstractHeaderWidget from "./abstractHeaderWidget.js";
import regionWidget from "../widgets/regionWidget.js";
import theme from "../theme.js";

/**
 * `headerWidget` is a subclass of `abstractHeaderWidget`.
 * In addition to its parent behavior, `headerWidget` handles differently
 * the branding, supports a back button, and is rendered differently.
 *
 * @param {{}} spec - Parameter object
 * @param {object} my - protected fields holder
 * @return {*}
 */
function headerWidget(spec, my) {
	spec ||= {};
	my ||= {};

	const that = abstractHeaderWidget(spec, my);

	my.headerButtonsWidget = regionWidget({
		content: (html) =>
			html.render(my.headerButtonsRenderer({ mobileOnly: false })),
	});

	my.headerButtonsOnMobileWidget = regionWidget({
		content: (html) =>
			html.render(my.headerButtonsRenderer({ mobileOnly: true })),
	});

	that.updateHeaderButtons = function () {
		my.headerButtonsWidget.update();
		my.headerButtonsOnMobileWidget.update();
	};

	that.renderContentOn = function (html) {
		html.div(
			{ klass: "header" },
			html.div(
				{
					id: "main-navbar",
					class: "navbar navbar-fixed-top",
				},
				html
					.div({ id: "leftmenu-toggler" })
					.click(my.onLeftTogglerClick.trigger),
				html.div(
					{ id: "brand-name" },
					html.h1({ class: "hide-sm" }, my.renderFullBrandingNameOn),
					html.h1(
						{ class: "mobile-header-title" },
						my.renderPageTitleOn,
					),
				),
				my.quickButtonsRenderer({ mobileOnly: true }),
			),
			my.renderAdminBarOn,
		);
	};

	my.renderAdminBarOn = function (html) {
		html.div(
			{
				id: "admin-navbar",
				klass: "navbar navbar-fixed-top hide-sm fp-tool-buttons",
			},
			my.renderBackButtonOn,
			html.h1(my.renderPageTitleOn),
			my.quickButtonsRenderer(),
		);
	};

	my.renderBackButtonOn = function (html) {
		const doc = that.getDocument();
		const backAction = doc && doc.backAction;

		if (!backAction) {
			return;
		}

		html.div(
			{ class: "backbutton-container" },
			html
				.a(
					html.div(
						{ class: "img-circle header-icon fp-back" },
						html.p(backAction.label),
					),
				)
				.click(() => {
					my.redirectTo(backAction.route, {});
				}),
		);
	};

	my.renderFullBrandingNameOn = function (html) {
		const brandingName = theme.brandingName;
		html.render(brandingName);
	};

	my.renderPageTitleOn = function (html) {
		if (!that.getDocument()) {
			return;
		}

		const title = that.getDocument().pageTitle;

		if (!title) {
			throw new Error("Missing page title");
		}

		html.render(title);
	};

	my.headerButtonsRenderer = function ({ mobileOnly }) {
		return (html) => {
			const doc = that.getDocument();

			if (!doc) {
				return;
			}

			let renderable = doc.headerButtons || (() => {});

			if (mobileOnly) {
				renderable = doc.headerButtonsOnMobile || renderable;
			}

			html.render(renderable);
		};
	};

	my.quickButtonsRenderer = function (options) {
		options ||= {};

		const mobileOnly = options.mobileOnly;
		const document = that.getDocument();

		if (!document) {
			return "";
		}

		const searchField = mobileOnly
			? document.mobileSearchField
			: document.searchField;
		return function (html) {
			html.div(
				{
					"data-test": "quickbuttons",
					class: [
						"client-quickbuttons-container",
						{ "show-ipad fp-tool-buttons": mobileOnly },
					],
				},
				(html) => {
					if (mobileOnly) {
						html.render(my.headerButtonsOnMobileWidget);
					} else {
						html.render(my.headerButtonsWidget);
					}
					if (searchField) {
						html.render(my.searchTogglerRenderer(searchField));
					}
				},
			);

			if (searchField) {
				html.render(searchField);
			}
		};
	};

	my.searchTogglerRenderer = function (searchField) {
		return function (html) {
			html.div({
				klass: "img-circle pull-right header-icon fp-search",
				"hover-tooltip": _("Search"),
				"hover-tooltip-container": "body",
				"data-original-title": "",
				title: _("Search"),
				"data-test": "toggle-search",
			}).click(() => {
				searchField.toggleSearchFieldVisibility();
			});
		};
	};

	return that;
}

export default headerWidget;
